import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Review } from '../../types';
import { getImageUrl } from '../../utils/imageUrl';
import StarRating from './StarRating';
import './ReviewList.css';

interface ReviewListProps {
    reviews: Review[];
    loading?: boolean;
}

const ITEMS_PER_PAGE = 4;

const ReviewList: React.FC<ReviewListProps> = ({ reviews, loading }) => {
    const { t, i18n } = useTranslation();
    const isBn = i18n.language === 'bn';

    const [activeFilter, setActiveFilter] = useState<string>('all'); // 'all', '5', '4', '3', '2', '1', 'photo'
    const [sortBy, setSortBy] = useState<string>('newest'); // 'newest', 'highest', 'lowest'
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [lightboxImage, setLightboxImage] = useState<string | null>(null);

    // Calculate rating distribution
    const ratingStats = useMemo(() => {
        if (!reviews.length) return { average: 0, count: 0, distribution: [0, 0, 0, 0, 0] };
        
        const total = reviews.length;
        const sum = reviews.reduce((acc, r) => acc + r.rating, 0);
        const avg = sum / total;
        
        const counts = [0, 0, 0, 0, 0]; // 5, 4, 3, 2, 1 star counts
        reviews.forEach((r) => {
            const index = 5 - Math.min(5, Math.max(1, Math.round(r.rating)));
            counts[index] += 1;
        });

        return {
            average: parseFloat(avg.toFixed(1)),
            count: total,
            distribution: counts,
        };
    }, [reviews]);

    // Filter & Sort reviews
    const processedReviews = useMemo(() => {
        let filtered = [...reviews];

        if (activeFilter === 'photo') {
            filtered = filtered.filter((r) => !!r.image);
        } else if (['1', '2', '3', '4', '5'].includes(activeFilter)) {
            const targetRating = parseInt(activeFilter, 10);
            filtered = filtered.filter((r) => r.rating === targetRating);
        }

        filtered.sort((a, b) => {
            if (sortBy === 'highest') return b.rating - a.rating;
            if (sortBy === 'lowest') return a.rating - b.rating;
            // Default newest
            return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
        });

        return filtered;
    }, [reviews, activeFilter, sortBy]);

    // Reset page to 1 whenever filter or sort changes
    const totalPages = Math.ceil(processedReviews.length / ITEMS_PER_PAGE);
    const paginatedReviews = useMemo(() => {
        const start = (currentPage - 1) * ITEMS_PER_PAGE;
        return processedReviews.slice(start, start + ITEMS_PER_PAGE);
    }, [processedReviews, currentPage]);

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
        const section = document.getElementById('reviews');
        if (section) {
            section.scrollIntoView({ behavior: 'smooth' });
        }
    };

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleDateString(isBn ? 'bn-BD' : 'en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    if (loading) {
        return (
            <div className="review-list-loading">
                <div className="loading-spinner"></div>
                <p>{t('reviews.loading')}</p>
            </div>
        );
    }

    if (reviews.length === 0) {
        return (
            <div className="review-list-empty">
                <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                </svg>
                <h3>{t('reviews.noReviews')}</h3>
                <p>{t('reviews.beFirst')}</p>
            </div>
        );
    }

    return (
        <div className="review-system-container">
            {/* Rating Executive Overview Box */}
            <div className="rating-overview-card">
                <div className="rating-score-box">
                    <div className="big-rating-number">{ratingStats.average}</div>
                    <div className="score-stars-col">
                        <StarRating rating={ratingStats.average} readOnly size="small" />
                        <span className="total-reviews-label">
                            {ratingStats.count} {ratingStats.count === 1 ? t('reviews.review') : t('reviews.reviews')}
                        </span>
                    </div>
                </div>

                <div className="rating-bars-container">
                    {[5, 4, 3, 2, 1].map((star, idx) => {
                        const count = ratingStats.distribution[idx];
                        const percentage = ratingStats.count ? Math.round((count / ratingStats.count) * 100) : 0;
                        return (
                            <div key={star} className="rating-bar-row">
                                <span className="star-level-label">{star}★</span>
                                <div className="rating-progress-track">
                                    <div
                                        className="rating-progress-fill"
                                        style={{ width: `${percentage}%` }}
                                    ></div>
                                </div>
                                <span className="bar-count-label">{count} ({percentage}%)</span>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Filter Pills & Sorting Bar */}
            <div className="reviews-filter-sort-bar">
                <div className="filter-pills-row">
                    <button
                        type="button"
                        className={`filter-pill ${activeFilter === 'all' ? 'active' : ''}`}
                        onClick={() => { setActiveFilter('all'); setCurrentPage(1); }}
                    >
                        {isBn ? 'সব রিভিউ' : 'All Reviews'} ({reviews.length})
                    </button>
                    <button
                        type="button"
                        className={`filter-pill ${activeFilter === '5' ? 'active' : ''}`}
                        onClick={() => { setActiveFilter('5'); setCurrentPage(1); }}
                    >
                        5★ ({ratingStats.distribution[0]})
                    </button>
                    <button
                        type="button"
                        className={`filter-pill ${activeFilter === '4' ? 'active' : ''}`}
                        onClick={() => { setActiveFilter('4'); setCurrentPage(1); }}
                    >
                        4★ ({ratingStats.distribution[1]})
                    </button>
                    <button
                        type="button"
                        className={`filter-pill ${activeFilter === 'photo' ? 'active' : ''}`}
                        onClick={() => { setActiveFilter('photo'); setCurrentPage(1); }}
                    >
                        📷 {isBn ? 'ছবিসহ' : 'With Photos'} ({reviews.filter(r => !!r.image).length})
                    </button>
                </div>

                <div className="sort-selector-wrapper">
                    <label htmlFor="review-sort-select">{isBn ? 'সাজান:' : 'Sort by:'}</label>
                    <select
                        id="review-sort-select"
                        value={sortBy}
                        onChange={(e) => { setSortBy(e.target.value); setCurrentPage(1); }}
                    >
                        <option value="newest">{isBn ? 'সর্বশেষ' : 'Most Recent'}</option>
                        <option value="highest">{isBn ? 'সর্বোচ্চ রেটিং' : 'Highest Rating'}</option>
                        <option value="lowest">{isBn ? 'সর্বনিম্ন রেটিং' : 'Lowest Rating'}</option>
                    </select>
                </div>
            </div>

            {/* Compact 2-Column Grid */}
            {paginatedReviews.length === 0 ? (
                <div className="no-filtered-reviews">
                    <p>{isBn ? 'এই ফিল্টারে কোন রিভিউ পাওয়া যায়নি।' : 'No reviews match the selected filter.'}</p>
                </div>
            ) : (
                <div className="review-compact-grid">
                    {paginatedReviews.map((review) => (
                        <div key={review.id} className={`compact-review-card ${review.image ? 'has-image' : ''}`}>
                            <div className="card-main-left">
                                <div className="compact-review-header">
                                    <div className="author-meta-block">
                                        <div className="author-avatar-sm">
                                            {review.customer_avatar ? (
                                                <img
                                                    src={getImageUrl(review.customer_avatar)}
                                                    alt={review.customer_name}
                                                    className="avatar-img-sm"
                                                />
                                            ) : (
                                                review.customer_name.charAt(0).toUpperCase()
                                            )}
                                        </div>
                                        <div className="author-text-info">
                                            <div className="author-name-row">
                                                <span className="compact-author-name">{review.customer_name}</span>
                                                <span className="verified-badge" title="Verified Customer">✓ {isBn ? 'যাচাইকৃত' : 'Verified'}</span>
                                            </div>
                                            <div className="author-sub-meta">
                                                <StarRating rating={review.rating} readOnly size="small" />
                                                <span className="compact-review-date">• {formatDate(review.created_at)}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="compact-review-body">
                                    <p className="compact-comment-text">{review.comment}</p>
                                </div>
                            </div>

                            {review.image && (
                                <div
                                    className="card-image-right"
                                    onClick={() => setLightboxImage(getImageUrl(review.image))}
                                    title={isBn ? 'ছবিটি বড় দেখতে ক্লিক করুন' : 'Click to view full photo'}
                                >
                                    <img
                                        src={getImageUrl(review.image)}
                                        alt="Review attachment"
                                        className="full-height-review-image"
                                    />
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {/* Pagination Controls */}
            {totalPages > 1 && (
                <div className="reviews-pagination-bar">
                    <button
                        type="button"
                        className="pagination-btn"
                        disabled={currentPage === 1}
                        onClick={() => handlePageChange(currentPage - 1)}
                    >
                        ‹ {isBn ? 'পূর্ববর্তী' : 'Previous'}
                    </button>

                    <div className="pagination-numbers">
                        {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                            <button
                                key={page}
                                type="button"
                                className={`page-number-btn ${currentPage === page ? 'active' : ''}`}
                                onClick={() => handlePageChange(page)}
                            >
                                {page}
                            </button>
                        ))}
                    </div>

                    <button
                        type="button"
                        className="pagination-btn"
                        disabled={currentPage === totalPages}
                        onClick={() => handlePageChange(currentPage + 1)}
                    >
                        {isBn ? 'পরবর্তী' : 'Next'} ›
                    </button>
                </div>
            )}

            {/* Lightbox Photo Preview Modal */}
            {lightboxImage && (
                <div className="review-lightbox-backdrop" onClick={() => setLightboxImage(null)}>
                    <div className="review-lightbox-content" onClick={(e) => e.stopPropagation()}>
                        <button
                            type="button"
                            className="lightbox-close-btn"
                            onClick={() => setLightboxImage(null)}
                        >
                            ✕
                        </button>
                        <img src={lightboxImage} alt="Review full resolution" className="lightbox-full-img" />
                    </div>
                </div>
            )}
        </div>
    );
};

export default ReviewList;
